import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import SpriteUtils from "@/utils/sprite-utils";
import {Keys} from "@/constants/key";
import {Tr} from "@/i18n/i18n";
import {ShopTab} from "@/scenes.ui/shop-tab";
import CU from "@/utils/c-u";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import ArtifactStore from "@/store/artifact";
import {UpdateInfo} from "@/store/artifact/types";
import {SquareButton} from "@/scenes.ui/square-button";
import {ImageButton} from "@/scenes.ui/image-button";
import {CompactBasePanel} from "@/scenes.ui/compact-base-panel";
import {ArtifactSlot} from "@/scenes.ui/artifact-slot";
import PlayerStore from "@/store/player";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import SoundUtils from "@/utils/sound-utils";
import {BottomForcesPanel} from "@/scenes.ui/bottom-forces-panel";

export class GroupUpgradePanel extends CompactBasePanel {
    private tabs: ShopTab[] = [];
    private selectedTabIndex = 0;

    private initialCreatureId: number | null;
    private creatureId: number | null;
    private infoText!: Phaser.GameObjects.Text;

    private artifactContainer!: Container;
    private artifactUpdates: UpdateInfo[] = [];
    private currentPage = 0;
    private artifactSize = 3;
    private costContainer?: Container;
    private artifactSlots: ArtifactSlot[] = [];
    private activeArtifactIds: Set<number> = new Set();

    private algorithmSelectedIds: Set<number> = new Set();
    private leftArrow?: ImageButton;
    private rightArrow?: ImageButton;
    private buildingSound?: Phaser.Sound.BaseSound;
    constructor(scene: Scene, canvas: HTMLCanvasElement, title: string, container: Container, creatureId: number | null, artifactSlots: ArtifactSlot[]) {

        super(scene, canvas, container);
        this.setScale(1 / CU.S, 1 / CU.S);

        this.initialCreatureId = creatureId;
        this.creatureId = creatureId;
        this.artifactSlots = artifactSlots;
        this.buildingSound = SoundUtils.addSound(this.scene, Keys.SOUND_BUILDING);

        const titleText = TextUtils.addTextByFamily(this.scene, title, 0, -280, "#ffffff", 18, "normal", 400, Fonts.BENGALY_NORMAL);
        titleText.setOrigin(0.5, 0.5);
        titleText.setStroke('#1c0e0e', 5);
        this.add(titleText);

        const secondText = TextUtils.addTextByFamily(this.scene, Tr('group.upgrade.select'), 0, -240, "#d1b56b", 16, "normal", 400, Fonts.MYRIADPRO_REGULAR);
        secondText.setOrigin(0.5, 0);
        secondText.setStroke('#1c0e0e', 5);
        this.add(secondText);

        const blackZone = SpriteUtils.addSprite(this.scene, 0, -35, Keys.ATLAS_UI_GROUP_ARTIFACT, 'back');
        blackZone.setOrigin(0.5, 0);
        this.add(blackZone);

        const sep = SpriteUtils.addSprite(this.scene, 0, -125, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR_LARGE);
        SpriteUtils.setDispSize(sep, 350 * CU.S, 8 * CU.S);
        this.add(sep);

        const tabWidth = 173;
        const tabY = -150;
        const tabLabels = [Tr('group.upgrade.current'), Tr('group.upgrade.all')];

        tabLabels.forEach((label, i) => {
            const tabX = -tabWidth / 2 + i * tabWidth;
            const tab = new ShopTab(this.scene, tabX, tabY, label, tabWidth, i === 0, false, () => this.selectedTab(i), 15);
            this.tabs.push(tab);
            this.add(tab);
        });
    }

    public open(): void {
        super.open();
        this.reloadArtifacts();
        if (BottomForcesPanel.instance) {
            BottomForcesPanel.instance.visible = false;
        }
    }

    private selectedTab(index: number) {
        this.tabs.forEach((tab, i) => tab.setSelected(i === index));
        this.selectedTabIndex = index;
        this.creatureId = index === 1 ? null : this.initialCreatureId;
        this.activeArtifactIds.clear();
        if (this.creatureId == null) {
            for (const slot of this.artifactSlots) {
                slot.removeGroupUpgradeCheckbox?.();
            }
        }
        this.currentPage = 0;
        this.addArtifacts();
        this.updateCost();
        this.addUpgradeButton();

        this.reloadArtifacts();
    }

    public getAllArtifactsOfPlayer(): Promise<UpdateInfo[]> {
        const player = PlayerStore.getPlayer();
        const promises: Promise<UpdateInfo[]>[] = [];
        for (const creature of player.creatures) {
            promises.push(ArtifactStore.artifactsInfoByCreature(creature.id));
        }
        return Promise.all(promises).then(results => {
            return results.flat();
        });
    }

    private reloadArtifacts(): void {
        const promiseList: Promise<any>[] = [];
        const allPromise = this.creatureId != null
            ? ArtifactStore.artifactsInfoByCreature(this.creatureId)
            : this.getAllArtifactsOfPlayer(); //вск
        const selectedPromise = ArtifactStore.getGroupUpgradeAvailable(this.creatureId ?? undefined);
        Promise.all([allPromise, selectedPromise]).then(([all, selected]) => {
            const selectedIds = new Set(selected.map((u: UpdateInfo) => u.id));
            const merged = new Map<number, UpdateInfo>();
            for (const ui of all) merged.set(ui.id, ui);
            for (const ui of selected) merged.set(ui.id, ui);
            this.artifactUpdates = Array.from(merged.values());
            this.algorithmSelectedIds = selectedIds;
            for (const id of selectedIds) {
                this.activeArtifactIds.add(id);
            }
            const count = this.activeArtifactIds.size;
            const text = Tr('group.upgrade.count').replace('{count}', String(count));
            if (this.infoText) this.infoText.destroy();
            this.infoText = TextUtils.addTextByFamily(this.scene, text, 0, -110, '#ffffff', 16, "normal", 300, Fonts.MYRIADPRO_REGULAR);
            this.infoText.setOrigin(0.5, 0);
            this.infoText.setStroke('#1c0e0e', 5);
            this.add(this.infoText);
            this.currentPage = 0;
            this.addArtifacts();
            this.updateCost();
            this.addUpgradeButton();
            this.addCheckboxesToSlots();
        });
    }

    private addArtifacts(): void {
        if (this.artifactContainer) {
            this.artifactContainer.destroy();
        }
        this.artifactContainer = new Container(this.scene, 0, 0);
        this.add(this.artifactContainer);
        const visible = this.getVisibleArtifacts()
            .slice(this.currentPage * this.artifactSize, (this.currentPage + 1) * this.artifactSize);
        visible.forEach((art, i) => {
            const x = i * 80 - ((visible.length - 1) * 80) / 2;
            const icon = SpriteUtils.addSprite(this.scene, x, 0, art.atlasKey, art.currImageKey);
            icon.setScale(0.7).setOrigin(0.5);
            this.artifactContainer.add(icon);
        });
    }

    private addUpgradeButton(): void {
        const selected = this.artifactUpdates.filter(ui => this.activeArtifactIds.has(ui.id));
        const allAffordable = selected.every(ui => this.hasEnoughForUpgrade(ui));
        const hasArtifacts = selected.length > 0;
        const button = new SquareButton(this.scene, 0, 240, 0, Keys.UI_ART_GREEN_BUTTON, Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('uluchshit').toUpperCase(), false, undefined, undefined, undefined, 16, Fonts.MYRIADPRO_REGULAR);
        if (hasArtifacts) {
            button.setAlpha(1);
            button.setCallback(() => {
                if (!allAffordable) {
                    new FeedbackPanel(this.scene, Tr('ne.hvataet.resursov'), undefined, false).show();
                    return;
                }
                ArtifactStore.startGroupUpgrade(Array.from(this.activeArtifactIds))
                    .then(() => ArtifactStore.actualizeArtifacts())
                    .then(() => {
                        this.buildingSound?.play();
                        this.close();
                        this.activeArtifactIds.clear();
                    });
            });
        } else {
            button.setAlpha(0.5);
        }
        this.add(button);
    }

    private createArrow(x: number, y: number, direction: number, onClick: () => void): ImageButton {
        const key = direction === -1 ? Keys.UI_ARROW_LEFT : Keys.UI_ARROW_RIGHT;
        const button = new ImageButton(this.scene, x, y, Keys.ATLAS_PROFILE, key);
        button.setCallback(onClick);
        return button;
    }

    private addCheckboxesToSlots(): void {
        if (this.creatureId == null) return;
        for (const slot of this.artifactSlots) {
            const art = (slot as any).art;
            if (!art) continue;
            if (art.updatingTill && art.updatingTill > Date.now()) continue;
            const isSelected = this.algorithmSelectedIds.has(art.id);
            if (isSelected) {
                this.activeArtifactIds.add(art.id);
            }
            slot.showGroupUpgradeCheckbox((artifactId, active) => {
                if (active) {
                    this.activeArtifactIds.add(artifactId);
                } else {
                    this.activeArtifactIds.delete(artifactId);
                }
                this.updateCost();
            }, isSelected);
        }
    }

    private updateCost(): void {
        const total = {gold: 0, crystal: 0, elixir: 0};
        for (const ui of this.artifactUpdates) {
            if (!this.activeArtifactIds.has(ui.id) || !ui.amount) continue;
            total.gold += ui.amount.gold;
            total.crystal += ui.amount.crystal;
            total.elixir += ui.amount.elixir;
        }
        if (this.costContainer) {
            this.costContainer.destroy();
        }
        this.addCost(total);
        this.updateArtifactCountText();
        const shownArtifacts = this.getVisibleArtifacts();
        const pages = Math.ceil(shownArtifacts .length / this.artifactSize);
        const maxPage = Math.max(0, pages - 1);
        if (this.currentPage > maxPage) {
            this.currentPage = 0;
        }
        this.leftArrow?.destroy();
        this.rightArrow?.destroy();
        this.leftArrow = undefined;
        this.rightArrow = undefined;
        this.addArtifacts();
        if (pages > 1) {
            this.leftArrow = this.createArrow(-150, 0, -1, () => {
                if (this.currentPage > 0) {
                    this.currentPage--;
                    this.addArtifacts();
                }
            });
            this.rightArrow = this.createArrow(150, 0, 1, () => {
                const newMax = Math.floor((this.getVisibleArtifacts().length - 1) / this.artifactSize);
                if (this.currentPage < newMax) {
                    this.currentPage++;
                    this.addArtifacts();
                }
            });
            this.add(this.leftArrow);
            this.add(this.rightArrow);
        }
        this.addUpgradeButton();
    }

    private addCost(total: { gold: number, crystal: number, elixir: number }): void {
        this.costContainer = new Container(this.scene, 0, 0);
        const rows: { value: number, icon: string }[] = [];
        if (total.gold > 0) {
            rows.push({value: total.gold, icon: Keys.IMG_RES_GOLD_NO_SHADOW});
        }
        if (total.crystal > 0) {
            rows.push({value: total.crystal, icon: Keys.IMG_REWARD_CRYSTAL_RED});
        }
        if (total.elixir > 0) {
            rows.push({value: total.elixir, icon: Keys.IMG_RES_ELIXIR_NO_SHADOW});
        }
        let y = 80;
        for (const row of rows) {
            const icon = SpriteUtils.addSprite(this.scene, 0, 0, row.icon);
            icon.setScale(0.5);
            icon.setOrigin(0, 0.5);
            const text = TextUtils.addTextByFamily(this.scene, TextUtils.formatMoney(row.value), 0, 0, "#ffffff", 14, "normal", 300, Fonts.MYRIADPRO_REGULAR);
            text.setOrigin(0, 0.5);
            text.setStroke('#1c0e0e', 5);
            text.updateText();
            const spacing = 8;
            const fullWidth = icon.displayWidth + spacing + text.displayWidth;
            icon.setX(-fullWidth / 2);
            text.setX(icon.x + icon.displayWidth + spacing);
            const line = new Container(this.scene, 0, y, [icon, text]);
            this.costContainer.add(line);
            y += 35;
        }
        this.add(this.costContainer);
    }

    private updateArtifactCountText(): void {
        if (!this.infoText) {
            return;
        }
        const count = this.activeArtifactIds.size;
        const text = count === 0 ? Tr('group.upgrade.none') : Tr('group.upgrade.count').replace('{count}', String(count));
        this.infoText.setText(text);
    }

    private getVisibleArtifacts(): UpdateInfo[] {
        const full = this.artifactUpdates;
        const filtered = full.filter(a => this.activeArtifactIds.has(a.id));
        return filtered.length === full.length ? full : filtered;
    }

    private hasEnoughForUpgrade(ui: UpdateInfo): boolean {
        const player = PlayerStore.getPlayer();
        if (!ui.amount) {
            return true;
        }
        return (
            player.gold >= ui.amount.gold &&
            player.crystal >= ui.amount.crystal &&
            player.elixir >= ui.amount.elixir &&
            player.mithril >= ui.amount.mithril &&
            (!ui.requiredRunes?.some(r => !r.present) ?? true)
        );
    }

    public close(): void {
        super.close();
        if (BottomForcesPanel.instance) {
            BottomForcesPanel.instance.visible = true;
        }
        for (const slot of this.artifactSlots) {
            slot.removeGroupUpgradeCheckbox?.();
        }
    }
}
